import {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import * as authAPI from "../api/auth";
import * as userAPI from "../api/user";
import { useTranslation } from "react-i18next";
import {
  showSuccess,
  showError,
  showInfo,
  showApiError,
} from "../utils/toast";

const AuthContext = createContext();

const SESSION_REFRESH_INTERVAL = 10 * 60 * 1000; // 10 minutes

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [preferences, setPreferences] = useState({
    language: "en",
    theme: "light",
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const refreshIntervalRef = useRef(null);
  const { t, i18n } = useTranslation();

  const isAuthenticated = !!user;

  // Fetch user preferences (only for authenticated users)
  const fetchPreferences = useCallback(async () => {
    try {
      const prefs = await userAPI.getPreferences();
      setPreferences(prefs);

      // Sync i18n
      if (prefs.language && i18n.language !== prefs.language) {
        i18n.changeLanguage(prefs.language);
      }

      return prefs;
    } catch (err) {
      console.error("Failed to fetch preferences:", err);
      return null;
    }
  }, [i18n]);

  // Update user preferences
  const updatePreference = useCallback(
    async (keyOrData, value) => {
      const data =
        typeof keyOrData === "string"
          ? { [keyOrData]: value }
          : keyOrData;

      if (!isAuthenticated) {
        const nextPreferences = {
          ...preferences,
          ...data,
        };
        setPreferences(nextPreferences);

        if (data?.language) {
          i18n.changeLanguage(data.language);
        }

        return nextPreferences;
      }

      try {
        const updatedPrefs = await userAPI.updatePreferences(data);
        setPreferences(updatedPrefs);

        if (data.language) {
          i18n.changeLanguage(data.language);
        }

        showSuccess(t("common.success", "Success"));
        return updatedPrefs;
      } catch (err) {
        showError(
          t(
            "common.preferencesUpdateFailed",
            "Failed to update preferences"
          )
        );
        throw err;
      }
    },
    [i18n, isAuthenticated, preferences, t]
  );

  // Check current authentication status
  const checkAuth = useCallback(async () => {
    try {
      const userData = await authAPI.getCurrentUser();
      setUser(userData);
      setError(null);
      // Fetch preferences for authenticated user
      await fetchPreferences();
      return userData;
    } catch (err) {
      // Not authenticated or session expired
      setUser(null);
      if (err.response?.status !== 401) {
        console.error("Auth check failed:", err);
      }
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [fetchPreferences]);

  // Refresh user data
  const refreshUser = useCallback(async () => {
    try {
      const userData = await authAPI.getCurrentUser();
      setUser(userData);
      return userData;
    } catch (err) {
      console.error("Failed to refresh user:", err);
      return null;
    }
  }, []);

  // Start session refresh interval
  const startSessionRefresh = useCallback(() => {
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
    }

    refreshIntervalRef.current = setInterval(async () => {
      console.log("Refreshing session...");
      await refreshUser();
    }, SESSION_REFRESH_INTERVAL);
  }, [refreshUser]);

  // Stop session refresh interval
  const stopSessionRefresh = useCallback(() => {
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
      refreshIntervalRef.current = null;
    }
  }, []);

  // Login
  const login = useCallback(
    async (email, password) => {
      try {
        setError(null);
        const response = await authAPI.login(email, password);
        const userData = await authAPI.getCurrentUser();
        setUser(userData);
        await fetchPreferences();
        startSessionRefresh();
        const displayName = userData.username || userData.email;
        showSuccess(
          t("auth.welcomeBack", {
            name: displayName,
            defaultValue: "Welcome back, {{name}}!",
          })
        );
        return userData;
      } catch (err) {
        setError(err.userMessage || t("auth.loginFailed", "Login failed"));
        // Error displayed in AuthModal - no toast needed
        throw err;
      }
    },
    [startSessionRefresh, fetchPreferences, t],
  );

  // Logout
  const logout = useCallback(async () => {
    try {
      await authAPI.logout();
      setUser(null);
      stopSessionRefresh();
      showSuccess("Logged out successfully");
    } catch (err) {
      console.error("Logout failed:", err);
      // Still clear user data even if API call fails
      setUser(null);
      stopSessionRefresh();
      showError("Logout failed, but you've been signed out locally");
    }
  }, [stopSessionRefresh]);

  // Register
  const register = useCallback(
    async (userData) => {
      try {
        setError(null);
        const response = await authAPI.register(userData);
        
        // Check if email verification is required
        if (response.detail && response.detail.includes("Verification")) {
          // Email verification required - show message
          showInfo(
            "Registration successful! Please check your email to verify your account. " +
            "(In development, check the Django console for the verification link)",
            { autoClose: 8000 }
          );
          // Don't close modal yet - user needs to know about email
          return { requiresVerification: true };
        }
        
        // Try to auto-login after registration (when verification is optional)
        try {
          const newUser = await authAPI.getCurrentUser();
          setUser(newUser);
          startSessionRefresh();
          showSuccess(
            `Welcome, ${newUser.username}! Your account has been created.`,
          );
          return newUser;
        } catch (loginErr) {
          // Auto-login failed (probably email verification required)
          showInfo(
            "Registration successful! Please log in to continue.",
          );
          return { requiresVerification: false };
        }
      } catch (err) {
        setError(err.userMessage || "Registration failed");
        // Error displayed in AuthModal - no toast needed
        throw err;
      }
    },
    [startSessionRefresh],
  );

  // Update profile
  const updateProfile = useCallback(async (userData) => {
    try {
      const updatedUser = await authAPI.updateProfile(userData);
      setUser(updatedUser);
      showSuccess("Profile updated successfully");
      return updatedUser;
    } catch (err) {
      showApiError(err, "Failed to update profile");
      throw err;
    }
  }, []);

  // Change password
  const changePassword = useCallback(
    async (oldPassword, newPassword1, newPassword2) => {
      try {
        await authAPI.changePassword(
          oldPassword,
          newPassword1,
          newPassword2,
        );
        showSuccess("Password changed successfully");
      } catch (err) {
        // Re-throw to let modal handle error display
        throw err;
      }
    },
    [],
  );

  // Handle unauthorized events from API interceptors
  useEffect(() => {
    const handleUnauthorized = () => {
      setUser(null);
      stopSessionRefresh();
      showError("Session expired. Please log in again.");
    };

    window.addEventListener("auth:unauthorized", handleUnauthorized);
    return () => {
      window.removeEventListener(
        "auth:unauthorized",
        handleUnauthorized,
      );
    };
  }, [stopSessionRefresh]);

  // Check auth on mount
  useEffect(() => {
    checkAuth().then((user) => {
      if (user) {
        startSessionRefresh();
      }
    });
  }, [checkAuth, startSessionRefresh]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopSessionRefresh();
    };
  }, [stopSessionRefresh]);

  const value = {
    user,
    isAuthenticated,
    isLoading,
    error,
    preferences,
    login,
    logout,
    register,
    updateProfile,
    changePassword,
    checkAuth,
    refreshUser,
    fetchPreferences,
    updatePreference,
  };

  return (
    <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}
